import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { authRoutes } from "./AuthRoutes";
import { useAppDispatch, useAppSelector } from "../ReduxToolkit/Hooks";
import { IUser } from "../features/login/types";
import { user } from "../utils/Constant";
import PrivateRoute from "./PrivateRoute";
import LayoutRoutes from "./LayoutRoutes";
import useFetchUserProfile from "../hooks/useFetchUserProfile";
import LoginSimple from "../Pages/OtherPages/Authentication/LoginSimple/LoginSimple";
import ResetChangePassword from "../Pages/OtherPages/Authentication/ResetPassword/ResetPassword";
import { filterMenuThunk } from "../ReduxToolkit/Slice/MenuSlice";
import AlertModal from "../CommonElements/AlertModal";
import {
  hideEmployeeNotificationForm,
  setIsOpenAlertPicModal,
  showEmployeeNotificationForm,
} from "../features/employeeManagement/redux/slice/EmployeeManagementSlice";
import { alertManager } from "../utils/AlertManager";
import MenusPage from "../Pages/Menus";

const RouterContent = () => {
  const dispatch = useAppDispatch();
  const userItem = localStorage.getItem(user);
  const userData: IUser | null = userItem ? JSON.parse(userItem) : null;
  const location = useLocation();

  const employeeDetails = useFetchUserProfile(userData || {}, location);
  const { data, isSuccess, isFetching } = employeeDetails || {};

  const notificationState = useAppSelector(
    (state) => state.employeeManagement.employeeNotificationForm,
  );
  const shouldRefreshMenu = useAppSelector(
    (state) => state.menu.shouldRefreshMenu,
  );

  const handleOnConfirm = () => {
    alertManager.confirm();
    dispatch(hideEmployeeNotificationForm());
  };

  useEffect(() => {
    alertManager.registerHandler((params) => {
      dispatch(
        showEmployeeNotificationForm({
          ...params,
          source: params.source ?? null,
        }),
      );
    });
  }, [dispatch]);

  useEffect(() => {
    const pathsToAvoid = [
      "/login",
      "/register",
      "/employee-user",
      // "/user-management/user"
    ];
    if (pathsToAvoid.some((path) => location.pathname.startsWith(path))) return;

    // Wait until the new data arrives after refetch
    if (!isSuccess || isFetching) return;

    const profile = data?.data?.personal_info;
    const config = data?.data?.config;
    if (!profile || !userData) return;

    // Merge updated profile with previous local user data
    const updatedUser = {
      ...userData,
      ...profile,
    };

    // Save to localStorage for persistence
    localStorage.setItem("user", JSON.stringify(updatedUser));
    localStorage.setItem("userConfig", JSON.stringify(config));

    // Trigger sidebar menu update
    dispatch(
      filterMenuThunk({
        user: updatedUser,
        userMenus: data?.data?.user_menus,
      }),
    );

    if (
      !employeeDetails?.data?.data?.personal_info?.profile &&
      !["/login", "/register"].includes(location.pathname)
    ) {
      dispatch(setIsOpenAlertPicModal(true));
    }
  }, [isSuccess, isFetching, data, location.pathname, shouldRefreshMenu]);

  return (
    <>
      <Routes>
        <Route path="/" element={<LoginSimple />} />
        <Route
          path="/authentication/resetpassword/:userId/:email/:expiry"
          element={<ResetChangePassword />}
        />
        <Route path={"/"} element={<PrivateRoute />}>
          <Route path={`/*`} element={<LayoutRoutes />} />
        </Route>
        {authRoutes.map(({ path, Component }, i) => (
          <Route path={path} element={Component} key={i} />
        ))}
        <Route path={`/login`} element={<LoginSimple />} />
      </Routes>

      <AlertModal
        message={notificationState.message || ""}
        show={notificationState.show}
        onConfirm={handleOnConfirm}
        title={notificationState.title}
        type={notificationState.type}
      />
    </>
  );
};

const RouterData = () => (
  <BrowserRouter basename={"/"}>
    <RouterContent />
  </BrowserRouter>
);

export default RouterData;
